/**
 * Client helpers for the threshold catalog endpoint.
 */

import {
  buildThresholdCatalogQuery,
  type ThresholdCatalogQuery,
  type ThresholdCatalogQueryBuilderInput,
} from './catalog-query';
import {
  transformThresholdCatalogResponse,
  type ApiThresholdCatalogResponse,
  type UIThresholdModel,
} from './catalog-transform';

// -- type definitions --

export interface FetchThresholdCatalogOptions {
  endpoint?: string;
  request: ThresholdCatalogQuery;
  signal?: AbortSignal;
  token?: string;
  fetchImpl?: typeof fetch;
}

// -- constants --

const DEFAULT_ENDPOINT = '/api/config/thresholds/catalog';

// -- functions --

/**
 * Fetches the threshold catalog and transforms the raw payload into the UI model.
 */
export async function fetchThresholdCatalog({
  endpoint = DEFAULT_ENDPOINT,
  request,
  signal,
  token,
  fetchImpl = fetch,
}: FetchThresholdCatalogOptions): Promise<UIThresholdModel[]> {
  const response = await fetchImpl(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `Threshold catalog request failed with status ${response.status}`
    );
  }

  const payload = (await response.json()) as ApiThresholdCatalogResponse;
  return transformThresholdCatalogResponse(payload);
}

/**
 * Convenience helper that builds the query from widget config and immediately fetches the catalog.
 */
export async function buildAndFetchThresholdCatalog(
  input: ThresholdCatalogQueryBuilderInput,
  options?: Omit<FetchThresholdCatalogOptions, 'request'>
): Promise<UIThresholdModel[]> {
  const request = buildThresholdCatalogQuery(input);
  return fetchThresholdCatalog({ ...options, request });
}
